import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSignInAlt, FaTachometerAlt, FaDownload, FaSyncAlt, FaPhoneAlt, FaArrowRight, FaArrowDown, FaUserCheck, FaFileDownload, FaHistory, FaHeadset, FaShieldAlt, FaChartLine, FaClock, FaCheckCircle, FaStar, FaLock , FaBell, FaChartBar } from 'react-icons/fa';
import { motion } from 'framer-motion';

const GettingStarted = ({ contentData }) => {
  const navigate = useNavigate();
  
  const steps = [
    {
      icon: <FaLock />,
      title: contentData?.qs_main_card1_title,
      description: contentData?.qs_main_card1_info,
      color: "from-blue-500 to-cyan-400",
      number: "01",
      tips: [contentData?.qs_main_card1_text1, contentData?.qs_main_card1_text2, contentData?.qs_main_card1_text3]
    },
    {
      icon: <FaTachometerAlt />,
      title: contentData?.qs_main_card2_title,
      description: contentData?.qs_main_card2_info,
      color: "from-violet-500 to-purple-400 ",
      number: "02",
      tips: [contentData?.qs_main_card2_text1, contentData?.qs_main_card2_text2, contentData?.qs_main_card2_text3]
    },
    {
      icon: <FaDownload />,
      title: contentData?.qs_main_card3_title,
      description: contentData?.qs_main_card3_info,
      color: "from-emerald-500 to-green-400",
      number: "03",
      tips: [contentData?.qs_main_card3_text1, contentData?.qs_main_card3_text2, contentData?.qs_main_card3_text3]
    },
    {
      icon: <FaBell />,
      title: contentData?.qs_main_card4_title,
      description: contentData?.qs_main_card4_info,
      color: "from-amber-500 to-orange-400",
      number: "04",
      tips: [contentData?.qs_main_card4_text1, contentData?.qs_main_card4_text2, contentData?.qs_main_card4_text3]
    }
  ];

  const stats = [
    { value: contentData?.qs_card1_title, label: contentData?.qs_card1_tex, icon: "⚡" },
    { value: "24/7", label: "Access", icon: "🌐" },
    { value: "100+", label: "Research Reports", icon: "📄" },
    { value: "Expert", label: "Support", icon: "👨‍💼" }
  ];

  return (
    <section className="relative py-20 bg-white overflow-hidden" id='getting-started'>
      {/* Minimal Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Geometric Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `linear-gradient(90deg, #000 1px, transparent 1px),
                             linear-gradient(180deg, #000 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        />
        
        {/* Floating Circles */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            animate={{
              y: [0, Math.sin(i) * 40, 0],
              x: [0, Math.cos(i) * 30, 0]
            }}
            transition={{
              duration: 15 + i * 2,
              repeat: Infinity,
              ease: "linear"
            }}
            className={`absolute rounded-full opacity-5 ${
              i % 4 === 0 ? 'bg-blue-400' : 
              i % 4 === 1 ? 'bg-emerald-400' : 
              i % 4 === 2 ? 'bg-violet-400' : 'bg-amber-400'
            }`}
            style={{
              width: `${100 + i * 40}px`,
              height: `${100 + i * 40}px`,
              left: `${(i * 12) % 100}%`,
              top: `${(i * 8) % 100}%`
            }}
          />
        ))}
        
        {/* Corner Accents */}
        <div className="absolute top-0 left-0 w-64 h-64 border-t-2 border-l-2 border-blue-100/30" />
        <div className="absolute bottom-0 right-0 w-64 h-64 border-b-2 border-r-2 border-emerald-100/30" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center mb-10">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-blue-50 to-emerald-50 rounded-full mb-6 border border-blue-100"
          >
            <span className="text-blue-600 font-semibold text-sm tracking-wider">
              QUICK START GUIDE
            </span>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
          >
            Get Started in 4 Simple Steps
            
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-gray-600 text-lg max-w-2xl mx-auto"
          >
            Follow this step-by-step guide to unlock premium research access
          </motion.p>
        </div>

    {/* Stats Bar */}
<motion.div
  initial={{ opacity: 0, y: 20 }}
  whileInView={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.6, delay: 0.3 }}
  viewport={{ once: true }}
  className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5"
>
  {[
    { icon: "⚡", value: contentData?.qs_card1_title, label: contentData?.qs_card1_text, color: "blue" },
    { icon: "🌐", value: contentData?.qs_card2_title, label: contentData?.qs_card2_text, color: "purple" },
    { icon: "📄", value: contentData?.qs_card3_title, label: contentData?.qs_card3_text, color: "green" },
    { icon: "👨‍💼", value: contentData?.qs_card4_title, label: contentData?.qs_card4_text, color: "orange" }
  ].map((stat, index) => (
    <motion.div
      key={index}
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
      viewport={{ once: true }}
      whileHover={{ 
        y: -4,
        scale: 1.03,
        transition: { duration: 0.15 }
      }}
      className="relative group cursor-pointer"
    >
      {/* Main Card Container */}
      <div className="relative overflow-hidden rounded-xl p-4 text-center transition-all duration-200">
        {/* Gradient Background */}
        <div className={`absolute inset-0 bg-gradient-to-br ${
          stat.color === 'blue' ? 'from-blue-500 to-blue-600' :
          stat.color === 'purple' ? 'from-purple-500 to-purple-600' :
          stat.color === 'green' ? 'from-green-500 to-green-600' :
          'from-orange-500 to-orange-600'
        } transition-all duration-300 group-hover:scale-105`}></div>
        
        {/* White Overlay on Hover */}
        <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
        
        {/* Border on Hover */}
        <div className={`absolute inset-0 border-2 ${
          stat.color === 'blue' ? 'border-blue-500' :
          stat.color === 'purple' ? 'border-purple-500' :
          stat.color === 'green' ? 'border-green-500' :
          'border-orange-500'
        } opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl pointer-events-none`}></div>
        
        {/* Content */}
        <div className="relative z-10">
          {/* Icon */}
          <motion.div
            className="text-2xl mb-2"
          >
            {stat.icon}
          </motion.div>
          
          {/* Value */}
          <div className="text-2xl font-bold mb-1 transition-colors duration-200">
            <span className="text-white group-hover:text-gray-900">
              {stat.value}
            </span>
          </div>
          
          {/* Label */}
          <div className="text-sm transition-colors duration-200">
            <span className="text-white group-hover:text-gray-600">
              {stat.label}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  ))}
</motion.div>

        {/* Steps Timeline */}
        <div className="relative">
          {/* Modern Timeline Line */}
          <div className="hidden lg:block absolute left-0 right-0 top-1/2 transform -translate-y-1/2 h-1">
            <div className="h-1 bg-gradient-to-r from-blue-100 via-emerald-100 to-violet-100 rounded-full mx-20">
              {/* Progress Dots */}
              {steps.map((_, index) => (
                <motion.div
                  key={index}
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="absolute w-4 h-4 bg-white border-2 border-blue-400 rounded-full transform -translate-y-1/2"
                  style={{ left: `${25 * (index + 1)}%` }}
                >
                  <div className="absolute inset-1 bg-blue-400 rounded-full"></div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Steps Grid */}
<div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
  {steps.map((step, index) => {
    // Define border colors based on step colors
    const borderColors = [
      "border-blue-500",
      "border-purple-500",
      "border-green-500",
      "border-orange-500"
    ];
    
    const textColors = [
      "text-blue-600",
      "text-purple-600",
      "text-green-600",
      "text-orange-600"
    ];
    
    return (
      <motion.div
        key={index}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        viewport={{ once: true }}
        className="relative group"
      >
        {/* Step Card with Border Hover Effect */}
        <div className="relative bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-200 h-full flex flex-col overflow-hidden">
          {/* Hover Border Overlay */}
          <div className={`absolute inset-0 rounded-2xl border-2 ${borderColors[index]} opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none`}></div>
          
          {/* Floating Number Badge */}
          <div className="absolute top-6 right-6 text-5xl font-bold text-gray-100/60 group-hover:text-gray-200 transition-colors duration-300">
            {step.number}
          </div>
          
          {/* Step Icon */}
          <motion.div
            whileHover={{ scale: 1.1, rotate: 5 }}
            transition={{ duration: 0.15 }}
            className={`w-16 h-16 mb-6 bg-gradient-to-r ${step.color} rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-200 relative z-10`}
          >
            <div className="text-white text-2xl">
              {step.icon}
            </div>
          </motion.div>
          
          {/* Step Content */}
          <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-gray-800 transition-colors duration-200 relative z-10">
            {step.title}
          </h3>
          <p className="text-gray-600 mb-6 flex-grow group-hover:text-gray-700 transition-colors duration-200 relative z-10">
            {step.description}
          </p>
          
          {/* Tips List */}
          <div className="space-y-2 mb-6 relative z-10">
            {step.tips.map((tip, tipIndex) => (
              <div key={tipIndex} className="flex items-center gap-2 group-hover:translate-x-1 transition-transform duration-200">
                <div className={`w-1.5 h-1.5 rounded-full transition-all duration-200 group-hover:scale-125 ${textColors[index].replace('text-', 'bg-')}`}></div>
                <span className="text-sm text-gray-500 group-hover:text-gray-600 transition-colors duration-200">
                  {tip}
                </span>
              </div>
            ))}
          </div>
          
          {/* Progress Indicator */}
          <div className="h-1 bg-gray-100 rounded-full overflow-hidden relative z-10">
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: "100%" }}
              transition={{ duration: 1, delay: index * 0.2 }}
              className={`h-full bg-gradient-to-r ${step.color} transition-all duration-200 group-hover:scale-y-125 group-hover:origin-left`}
            />
          </div>
          
          {/* Hover Arrow */}
          <motion.div
            initial={{ opacity: 0, x: -5 }}
            whileHover={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.15 }}
            className="absolute bottom-6 right-6 z-10"
          >
            <FaArrowRight className={textColors[index]} />
          </motion.div>
        </div>
        
        {/* Connection Line for Mobile */}
        {index < steps.length - 1 && (
          <div className="md:hidden flex justify-center mt-8 mb-8">
            <motion.div
              animate={{ y: [0, 5, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-gray-300"
            >
              <FaArrowDown className="text-xl" />
            </motion.div>
          </div>
        )}
      </motion.div>
    );
  })}
</div>
        </div>

        

        {/* Floating CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          viewport={{ once: true }}
          className="mt-10 flex justify-center"
        >
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(37, 99, 235, 0.2)" }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/login')}
            className="px-8 py-4 bg-gradient-to-r from-blue-600 to-emerald-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-3"
          >
            <span>Start Your Journey</span>
            <motion.div
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <FaArrowRight />
            </motion.div>
          </motion.button>
        </motion.div>
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            animate={{
              y: [0, -100],
              x: [0, Math.sin(i) * 30],
              opacity: [0, 1, 0]
            }}
            transition={{
              duration: 4 + Math.random() * 2,
              repeat: Infinity,
              delay: i * 0.3
            }}
            className="absolute w-0.5 h-0.5 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
          />
        ))}
      </div>
    </section>
  );
};

export default GettingStarted;